import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, hasRole } from "../../context/AuthContext";
import { api, extractError } from "../../lib/api";
import { SectionCard, Pill } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { FormActions, FormField, FormSection } from "../forms/FormField";
import { DEFAULT_PADDY_LAB_VALUES, PaddyLabAnalysisFields } from "../forms/PaddyLabAnalysisFields";
import { PartySelect } from "../forms/PartySelect";
import { StackSampleLineSelect } from "../forms/StackSampleLineSelect";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import LabTabShell from "./LabTabShell";
import StackSampleLabRegister from "./StackSampleLabRegister";
import { mapNumberFields, PADDY_LAB_NUMBER_KEYS } from "../../lib/numberInput";

export function StackSampleLabTab() {
  const { user } = useAuth();
  const { data, reload } = usePageBootstrap({
    path: "/lab/bootstrap",
    params: { tab: "stacks" },
    wsTypes: ["stack", "lab"],
  });
  useBootstrapWS(["stack", "lab"], reload);
  const parties = data?.parties || [];
  const pendingLines = data?.pending_lines || [];
  const stackSamples = data?.intakes || [];
  const contracts = data?.contracts || [];
  const labReports = data?.lab_reports || [];
  const [partyId, setPartyId] = useState("");
  const [sampleLineId, setSampleLineId] = useState("");
  const [form, setForm] = useState({ ...DEFAULT_PADDY_LAB_VALUES });
  const [submitting, setSubmitting] = useState(false);

  const [linkIntakeId, setLinkIntakeId] = useState("");
  const [linkPurchaseId, setLinkPurchaseId] = useState("");
  const [linkContractId, setLinkContractId] = useState("");

  const onPartyChange = (id) => {
    setPartyId(id);
    setSampleLineId("");
  };

  const submitLab = async (e) => {
    e.preventDefault();
    if (!sampleLineId) return toast.error("Select a sample ID");
    setSubmitting(true);
    try {
      await api.post(`/stack-sample-lines/${sampleLineId}/lab`, mapNumberFields(form, PADDY_LAB_NUMBER_KEYS));
      toast.success("Stack sample lab saved — sent to purchaser for approval");
      setSampleLineId("");
      setForm({ ...DEFAULT_PADDY_LAB_VALUES });
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!linkIntakeId) return toast.error("Intake required");
    try {
      await api.patch(`/stack-samples/${linkIntakeId}/link`, {
        linked_purchase_id: linkPurchaseId ? +linkPurchaseId : null,
        contract_id: linkContractId && linkContractId !== "none" ? +linkContractId : null,
      });
      toast.success("Links updated");
      setLinkIntakeId("");
      setLinkPurchaseId("");
      setLinkContractId("");
      reload();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const canLab = hasRole(user, ["admin", "lab"]);

  const record = (
    <>
      <SectionCard title="Stack sample analysis">
        <p className="text-sm text-muted-foreground mb-4">
          Select the party, then the pending sample ID for that party. Record one lab report per stack line.
          {" "}
          <Link to="/stack-sample-intake" className="text-primary underline-offset-2 hover:underline">
            Register new intake
          </Link>
        </p>
        {canLab ? (
          <form onSubmit={submitLab}>
            <FormSection title="Sample selection">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PartySelect
                  parties={parties}
                  value={partyId}
                  onChange={onPartyChange}
                  required
                  disabled={!parties.length}
                  testId="stack-lab-party"
                />
                <StackSampleLineSelect
                  lines={pendingLines}
                  partyId={partyId}
                  value={sampleLineId}
                  onChange={setSampleLineId}
                  required
                />
              </div>
            </FormSection>

            <FormSection title="Paddy quality analysis" className="mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <PaddyLabAnalysisFields values={form} onChange={setForm} testIdPrefix="stack-lab" />
              </div>
            </FormSection>

            <FormActions>
              <Button
                type="submit"
                disabled={submitting || !sampleLineId}
                className="bg-bansal-sage hover:bg-bansal-sageDark text-white sm:min-w-[180px]"
              >
                {submitting ? "Saving…" : "Save stack sample lab"}
              </Button>
            </FormActions>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">Sign in as lab or admin to record stack sample analysis.</p>
        )}
      </SectionCard>

      <div className="mt-6">
      <SectionCard title="Link purchase / contract">
        <p className="text-xs text-muted-foreground mb-4">
          After lab (<code className="text-[11px]">LAB_DONE</code>), attach purchase or contract references for paperwork.
        </p>
        {hasRole(user, ["admin", "manager"]) ? (
          <form onSubmit={submitLink}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <FormField label="Intake" required>
                <Select value={linkIntakeId || "_none"} onValueChange={(v) => setLinkIntakeId(v === "_none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select intake…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" disabled>Select intake…</SelectItem>
                    {stackSamples.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.code} ({s.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Purchase id (optional)">
                <Input value={linkPurchaseId} onChange={(e) => setLinkPurchaseId(e.target.value)} placeholder="PT id…" />
              </FormField>
              <FormField label="Contract (optional)">
                <Select value={linkContractId || "none"} onValueChange={(v) => setLinkContractId(v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contracts.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.contract_number || c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <div className="flex items-end">
                <Button type="submit" variant="outline" className="w-full">Update links</Button>
              </div>
            </div>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">Manager or admin only.</p>
        )}
      </SectionCard>
      </div>

      <div className="mt-6">
      <SectionCard title={`Stack sample intakes (${stackSamples.length})`}>
        <div className="space-y-2 max-h-[360px] overflow-y-auto text-sm">
          {stackSamples.length === 0 && <p className="text-muted-foreground py-4 text-center">No intakes yet.</p>}
          {stackSamples.map((s) => (
            <div key={s.id} className="border border-border rounded-lg p-3 bg-card flex flex-wrap justify-between gap-2">
              <div>
                <span className="font-mono text-xs font-semibold">{s.code}</span>
                <span className="ml-2">
                  <Pill tone={s.status === "LAB_DONE" ? "success" : s.status === "OPEN" ? "warning" : "default"}>
                    {s.status}
                  </Pill>
                </span>
                <div className="text-xs text-muted-foreground mt-1">
                  {(s.intake_timing === "AFTER_CONTRACT" ? "After" : "Before")} contract
                  {s.party_name ? ` · ${s.party_name}` : ""}
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {(s.stack_lines || s.stacks || []).map((x) => (
                    <div key={x.sample_code || x.line_id || x.stack_number}>
                      <span className="font-mono">{x.sample_code || s.code}</span>
                      {" — "}Stack {x.stack_number || x.stack_id} · {x.approval_status || x.lab_status || "—"}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
      </div>
    </>
  );

  const register = <StackSampleLabRegister reports={labReports} />;

  return <LabTabShell record={record} register={register} registerCount={labReports.length} testId="stack-lab-tabs" />;
}
